type Props = {
    children: React.ReactNode;
    href?: string;
    target?: string;
    callback?: () => void;
    className?: string;
    /** Nombre accesible cuando el enlace sólo lleva un icono dentro. */
    ariaLabel?: string;
};

/**
 * Enlace sin estilos: pinta una `<a>` de verdad con su href, así que el
 * navegador puede abrirla en otra pestaña o ventana (clic derecho, ⌘/Ctrl+clic,
 * botón central) y el teclado la activa con Enter.
 *
 * Sólo intercepta el clic primario sin modificadores, que es el que debe
 * navegar con el router en vez de recargar la página; el resto se deja pasar
 * tal cual. Con `target` explícito no intercepta nada: ahí se quiere que abra
 * el navegador.
 *
 * ReactNode children
 * String href - destino del enlace
 * String target - "_blank", "_new"… deja que el navegador se encargue del clic
 * Function callback - navegación con el router para el clic primario
 * String className - clases del propio enlace, para los que ocupan su hueco
 * String ariaLabel - nombre accesible si dentro sólo va un icono
 */
export default function LinkWrapper({
    children,
    href,
    target,
    callback,
    className,
    ariaLabel,
}: Props): React.ReactElement {
    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
        if (!callback) return;

        // Alguien de dentro ya se ocupó del clic.
        if (event.defaultPrevented) return;

        // Con target la navegación es cosa del navegador.
        if (target) return;

        // Botón central/secundario y clic con modificadores: abrir en otra
        // pestaña o ventana es responsabilidad del navegador.
        if (event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        event.preventDefault();
        callback();
    };

    return (
        <a
            href={href}
            target={target}
            onClick={handleClick}
            className={className}
            aria-label={ariaLabel}
        >
            {children}
        </a>
    );
}
